import { JAVA_API_STATUS } from '../../utils/axios-interceptor';
import { assertPdfBlob } from '../../utils/pdfResponseGuard';
import { extractFilenameFromContentDisposition } from '../../utils/downloadUtils';
import { getConsentHistory, downloadConsentAuditTrail } from './consentAuditService';
import { CONSENT_AUDIT_TRAIL_FALLBACK_FILENAME } from './constants';
import { ConsentHistoryEntry, ConsentQueryParams } from './types';
import { Handlers } from '../../types/actions/types';

export type ConsentHistoryFetchResult =
    | { success: true; entries: ConsentHistoryEntry[]; orgName: string }
    | { success: false; message?: string };

// Resolves the /consent/history responseCode so the hook only deals with success/failure, not
// the raw envelope shape. Callers may also pass handlers: onSuccess(entries) or onError().
export async function fetchConsentHistory(params: ConsentQueryParams, handlers?: Handlers): Promise<ConsentHistoryFetchResult> {
    const result = await resolveConsentHistory(params);
    if (result.success) handlers?.onSuccess?.(result.entries);
    else handlers?.onError?.();
    return result;
}

async function resolveConsentHistory(params: ConsentQueryParams): Promise<ConsentHistoryFetchResult> {
    try {
        const res = await getConsentHistory(params);
        if (res?.data?.responseData?.responseCode === JAVA_API_STATUS.SUCCESS) {
            return { success: true, entries: res.data?.data?.entries ?? [], orgName: res.data?.data?.orgName ?? '' };
        }
        return { success: false, message: res?.data?.responseData?.responseMessage };
    } catch (err) {
        // Preserve the underlying error, same as the sibling StatusTimelineApiHelper — the toast
        // above only shows a generic message, so this is the only signal a broken /consent/history
        // call leaves behind.
        console.error('Error fetching consent history:', err);
        return { success: false };
    }
}

export interface ConsentAuditTrailFile {
    blob: Blob;
    filename: string;
}

// Downloads the audit trail blob and resolves it into a ready-to-save file, or `false` on any
// failure — including a passing HTTP request that isn't actually a PDF (see assertPdfBlob).
export async function fetchConsentAuditTrailFile(params: ConsentQueryParams): Promise<ConsentAuditTrailFile | false> {
    try {
        const res = await downloadConsentAuditTrail(params);
        const blob = new Blob([res.data], { type: 'application/pdf' });
        await assertPdfBlob(blob);
        const filename =
            extractFilenameFromContentDisposition(res.headers?.['content-disposition']) ?? CONSENT_AUDIT_TRAIL_FALLBACK_FILENAME;
        return { blob, filename };
    } catch (err) {
        // Preserve the underlying error — could be the axios request itself, or assertPdfBlob
        // rejecting a 200-with-JSON-error response (aica's non-4xx failure shape for this
        // endpoint). Either way, a bare catch here leaves zero signal beyond a user complaint.
        console.error('Error downloading consent audit trail:', err);
        return false;
    }
}
